import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Laptop, ShieldAlert } from 'lucide-react';
import { api } from '../lib/api';
import { useMe } from '../hooks/useMe';
import { Button, Card, CardBody, ErrorState, Notice, Spinner } from '../components/ui';
import { relativeTime } from '../lib/format';

/** Approve "Sign in with your browser" from the desktop app. */
export function LinkDevicePage() {
  const [params] = useSearchParams();
  const code = (params.get('code') ?? '').toUpperCase();
  const me = useMe().data;
  const [declined, setDeclined] = useState(false);
  const req = useQuery({ queryKey: ['device-login', code], queryFn: () => api('GET /api/auth/device/:code', { params: { code } }), enabled: !!code, retry: false });
  const approve = useMutation({ mutationFn: () => api('POST /api/auth/device/approve', { body: { code } }) });

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto p-4">
      <Card className="w-full max-w-md">
        <CardBody className="space-y-5 p-6 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary-ink">
            <Laptop className="h-7 w-7" />
          </span>
          {!code ? (
            <Notice tone="warn">This link is missing its code. Start again from the MailPilot app.</Notice>
          ) : req.isPending ? (
            <Spinner label="Checking the request" />
          ) : req.error ? (
            <ErrorState error={req.error} title="This sign-in request isn't valid any more" compact />
          ) : approve.isSuccess ? (
            <div className="space-y-2">
              <CheckCircle2 className="mx-auto h-8 w-8 text-success" />
              <h1 className="text-lg font-semibold text-ink">{req.data?.deviceName} is signed in</h1>
              <p className="text-[0.8125rem] text-muted">You can close this tab and go back to the MailPilot app.</p>
            </div>
          ) : declined ? (
            <div className="space-y-2">
              <ShieldAlert className="mx-auto h-8 w-8 text-warn" />
              <h1 className="text-lg font-semibold text-ink">Not signed in</h1>
              <p className="text-[0.8125rem] text-muted">Nothing was shared. If you didn't start this, someone may be trying to get into your account — you can ignore it.</p>
              <Link to="/inbox" className="inline-block text-[0.8125rem] font-semibold text-primary-ink hover:underline">Go to your inbox</Link>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <h1 className="text-lg font-semibold text-ink">Sign in MailPilot on {req.data?.deviceName}?</h1>
                <p className="text-[0.8125rem] text-muted">
                  As <b className="text-ink">{me?.user.email}</b> · requested {relativeTime(req.data?.createdAt)}
                </p>
              </div>
              <div className="rounded-2xl border border-line bg-raised p-3">
                <p className="text-xs text-muted">Check that the app shows the same code</p>
                <p className="mt-1 font-mono text-2xl font-semibold tracking-[0.2em] text-ink">{code}</p>
              </div>
              <p className="text-xs text-muted">Only approve this if you just clicked “Sign in with your browser” in the MailPilot app on your own computer.</p>
              <div className="flex gap-2">
                <Button block onClick={() => setDeclined(true)}>
                  This wasn't me
                </Button>
                <Button block variant="primary" loading={approve.isPending} onClick={() => approve.mutate()}>
                  Approve
                </Button>
              </div>
              {approve.error ? <ErrorState error={approve.error} compact /> : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
